import axios from 'axios';
import { txApiClient } from './txClient';
import type { 
  CreateSessionRequest, 
  CreateSessionResponse, 
  ChatMessage, 
  SessionFile,
  VaultFile
} from '../types/session.types';

export const createSession = async (req: CreateSessionRequest): Promise<CreateSessionResponse> => {
  // Session creation involves container provisioning + asset downloads from Data Utility,
  // which can take longer than the default 30s timeout.
  const { data } = await txApiClient.post('/api/sessions', req, { timeout: 120000 });
  return data;
};

export const sendMessage = async (sessionId: string, content: string, historyRefs?: string[], vaultFileRefs?: string[]): Promise<void> => {
  try {
    await txApiClient.post(`/api/sessions/${sessionId}/messages`, {
      parts: [{ type: 'text', text: content }],
      ...(historyRefs && historyRefs.length > 0 ? { historyRefs } : {}),
      ...(vaultFileRefs && vaultFileRefs.length > 0 ? { vaultFileRefs } : {}),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      const data = error.response.data;
      if (data?.error?.code === 'TURN_LIMIT_EXCEEDED') {
        const turnLimitError = new Error('Turn limit exceeded');
        (turnLimitError as any).code = 'TURN_LIMIT_EXCEEDED';
        throw turnLimitError;
      }
    }
    throw error;
  }
};

export const stopGeneration = async (sessionId: string): Promise<void> => {
  await txApiClient.post(`/api/sessions/${sessionId}/stop`);
};

export const resetSession = async (sessionId: string): Promise<void> => {
  await txApiClient.post(`/api/sessions/${sessionId}/reset`);
};

export const terminateSession = async (sessionId: string): Promise<void> => {
  await txApiClient.delete(`/api/sessions/${sessionId}`);
};

export const getHistory = async (sessionId: string): Promise<ChatMessage[]> => {
  const { data } = await txApiClient.get(`/api/sessions/${sessionId}/history`);
  return data.messages || data.data?.messages || [];
};

export const uploadSessionFiles = async (
  sessionId: string, 
  files: File[], 
  onProgress?: (pct: number) => void
): Promise<{ success: boolean, files: Array<{ fileId: string; filename: string; sizeBytes: number; status: string }> }> => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  
  const { data } = await txApiClient.post(`/api/sessions/${sessionId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => onProgress?.(Math.round((e.loaded * 100) / (e.total ?? 1))),
  });
  return data;
};

export const listSessionFiles = async (sessionId: string): Promise<SessionFile[]> => {
  const { data } = await txApiClient.get(`/api/sessions/${sessionId}/files`);
  return data.files;
};

export const downloadFile = async (fileId: string, filename: string): Promise<void> => {
  const response = await txApiClient.get(`/api/files/download/${encodeURIComponent(fileId)}`, { 
    responseType: 'blob' 
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadOutputFile = async (sessionId: string, filename: string): Promise<void> => {
  const response = await txApiClient.get(`/api/sessions/${sessionId}/output/${encodeURIComponent(filename)}`, { 
    responseType: 'blob' 
  });
  
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};


export const deleteFile = async (fileId: string): Promise<void> => {
  await txApiClient.delete(`/api/files/delete/${encodeURIComponent(fileId)}`);
};

/**
 * Permanently purges a file from the S3 vault.
 * Distinct from deleteFile, which only removes the session-level reference.
 * Calls DELETE /api/files/delete/:id?flush=true on the Transaction BFF.
 */
export const flushS3File = async (fileId: string): Promise<void> => {
  await txApiClient.delete(`/api/files/delete/${encodeURIComponent(fileId)}?flush=true`);
};

export const respondToPermission = async (permissionId: string, approved: boolean): Promise<void> => {
  await txApiClient.post(`/api/permissions/${permissionId}/respond`, {
    decision: approved ? 'allow' : 'deny',
  });
};

export const respondToQuestion = async (questionId: string, answer: string): Promise<void> => {
  await txApiClient.post(`/api/questions/${questionId}/respond`, { answer });
};

export const listChatHistory = async (): Promise<Array<{ fileId: string; filename: string; displayName: string; agentName: string; timestamp: string; sizeBytes: number }>> => {
  const { data } = await txApiClient.get('/api/chat-history');
  return data?.summaries || [];
};

export const triggerChatSummary = async (sessionId: string): Promise<void> => {
  await txApiClient.post(`/api/sessions/${sessionId}/summary`);
};

export const listVaultFiles = async (limit = 200): Promise<VaultFile[]> => {
  const { data } = await txApiClient.get('/api/files', { params: { limit } });
  return (data?.files || []).map((f: any) => ({
    fileId: f.fileId,
    filename: f.filename,
    sizeBytes: f.sizeBytes,
    lastModified: f.generatedAt,
  }));
};

export const stageVaultFiles = async (
  sessionId: string,
  vaultKeys: string[]
): Promise<{ success: boolean, files: Array<{ fileId: string; filename: string; sizeBytes: number; status: string }> }> => {
  const { data } = await txApiClient.post(`/api/sessions/${sessionId}/files`, { vaultKeys });
  return data;
};
